import { useCallback, useRef, useState } from 'react';

import { getLabel } from '../../../api/submission_details';
import { HttpResponse } from '../../Snapshot/constant';
import { SnapshotComparisonDetailRow } from '../types';
import { ComparePdfViewMode, parseSubmission, sectionParamFromViewMode } from '../utils';

type PdfSide = 'previous' | 'current';

type ComparePdfState = {
  previousPdfUrl: string;
  previousPdfLoading: boolean;
  previousPdfError: string;
  currentPdfUrl: string;
  currentPdfLoading: boolean;
  currentPdfError: string;
};

const INITIAL_PDF_STATE: ComparePdfState = {
  previousPdfUrl: '',
  previousPdfLoading: false,
  previousPdfError: '',
  currentPdfUrl: '',
  currentPdfLoading: false,
  currentPdfError: ''
};

const LABEL_ERROR_MESSAGES: Record<PdfSide, string> = {
  previous: 'Could not load previous submission label.',
  current: 'Could not load current submission label.'
};

/** Fetches side-by-side label PDFs for the Compare modal via submission_details. */
const useSnapshotComparisonComparePdfs = (applicationNumber: string) => {
  const [open, setOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<SnapshotComparisonDetailRow | null>(null);
  const [pdfViewMode, setPdfViewMode] = useState<ComparePdfViewMode>('clinical_studies');
  const [pdfState, setPdfState] = useState<ComparePdfState>(INITIAL_PDF_STATE);
  // Ignores stale responses when the user switches tabs or closes the modal quickly.
  const loadGenerationRef = useRef(0);

  const updatePdfState = useCallback((updates: Partial<ComparePdfState>) => {
    setPdfState(previous => ({ ...previous, ...updates }));
  }, []);

  const fetchLabelPdf = useCallback(
    async (
      side: PdfSide,
      applno: string,
      submission: string | null,
      documentId: number | null,
      center: string | null,
      section: string | undefined,
      generation: number
    ) => {
      const parsed = parseSubmission(submission);
      // Compare requires document IDs; escalated rows may have null IDs and show an error.
      if (!parsed || !center || documentId == null) {
        if (generation !== loadGenerationRef.current) {
          return;
        }

        updatePdfState(
          side === 'previous'
            ? { previousPdfError: LABEL_ERROR_MESSAGES.previous, previousPdfLoading: false }
            : { currentPdfError: LABEL_ERROR_MESSAGES.current, currentPdfLoading: false }
        );
        return;
      }

      try {
        const response = await getLabel(
          applno,
          parsed.submissionType,
          Number(parsed.submissionNumber),
          center,
          documentId,
          section
        );

        if (generation !== loadGenerationRef.current) {
          return;
        }

        // submission_details uses `Status`/`Success`; snapshot_change_detection uses lowercase `status`.
        if (response?.data?.Status === HttpResponse.OK && response?.data?.Success?.pdf_link) {
          updatePdfState(
            side === 'previous'
              ? {
                  previousPdfUrl: response.data.Success.pdf_link,
                  previousPdfError: '',
                  previousPdfLoading: false
                }
              : {
                  currentPdfUrl: response.data.Success.pdf_link,
                  currentPdfError: '',
                  currentPdfLoading: false
                }
          );
          return;
        }

        updatePdfState(
          side === 'previous'
            ? { previousPdfError: LABEL_ERROR_MESSAGES.previous, previousPdfLoading: false }
            : { currentPdfError: LABEL_ERROR_MESSAGES.current, currentPdfLoading: false }
        );
      } catch {
        if (generation !== loadGenerationRef.current) {
          return;
        }

        updatePdfState(
          side === 'previous'
            ? { previousPdfError: LABEL_ERROR_MESSAGES.previous, previousPdfLoading: false }
            : { currentPdfError: LABEL_ERROR_MESSAGES.current, currentPdfLoading: false }
        );
      }
    },
    [updatePdfState]
  );

  const loadPdfsForRow = useCallback(
    (row: SnapshotComparisonDetailRow, mode: ComparePdfViewMode) => {
      loadGenerationRef.current += 1;
      const generation = loadGenerationRef.current;
      const section = sectionParamFromViewMode(mode);

      updatePdfState({
        previousPdfUrl: '',
        previousPdfError: '',
        previousPdfLoading: true,
        currentPdfUrl: '',
        currentPdfError: '',
        currentPdfLoading: true
      });

      // Previous side uses compared_to_* fields; current side uses submission + current_document_id.
      fetchLabelPdf(
        'previous',
        applicationNumber,
        row.compared_to_submission,
        row.compared_to_document_id,
        row.center,
        section,
        generation
      );
      fetchLabelPdf(
        'current',
        applicationNumber,
        row.submission,
        row.current_document_id,
        row.center,
        section,
        generation
      );
    },
    [applicationNumber, fetchLabelPdf, updatePdfState]
  );

  const openCompare = useCallback(
    (row: SnapshotComparisonDetailRow) => {
      setSelectedRow(row);
      setPdfViewMode('clinical_studies');
      setOpen(true);
      loadPdfsForRow(row, 'clinical_studies');
    },
    [loadPdfsForRow]
  );

  const closeCompare = useCallback(() => {
    loadGenerationRef.current += 1;
    setOpen(false);
    setSelectedRow(null);
    setPdfState(INITIAL_PDF_STATE);
  }, []);

  const changePdfViewMode = useCallback(
    (mode: ComparePdfViewMode) => {
      setPdfViewMode(mode);
      if (selectedRow) {
        loadPdfsForRow(selectedRow, mode);
      }
    },
    [loadPdfsForRow, selectedRow]
  );

  return {
    open,
    selectedRow,
    pdfViewMode,
    ...pdfState,
    openCompare,
    closeCompare,
    changePdfViewMode
  };
};

export default useSnapshotComparisonComparePdfs;
